"use client";

import { useCallback, useEffect, useRef, useState, useSyncExternalStore } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { cn } from "@/lib/utils";
import { ReviewCard } from "./ReviewCard";
import type { DisplayReview } from "@/lib/review-wall";

/**
 * Horizontal review carousel for the home page's "What Our Customers Say"
 * section. Deliberately mirrors ProductCarouselClient's interaction model
 * (native scroll-snap scroller, card-step prev/next, autoplay that pauses on
 * hover/focus/hidden-tab/reduced-motion) rather than adding a carousel library —
 * consistent behaviour, and no new dependency in the Worker bundle.
 *
 * The cards themselves are the SHARED `<ReviewCard />` that /reviews renders,
 * so the two surfaces can't drift apart visually.
 */

const CARD_GAP_PX = 16; // matches `gap-4` in the row
const reducedMotionQuery = "(prefers-reduced-motion: reduce)";

function subscribeToReducedMotion(onChange: () => void) {
  const mediaQuery = window.matchMedia(reducedMotionQuery);
  mediaQuery.addEventListener?.("change", onChange);
  return () => mediaQuery.removeEventListener?.("change", onChange);
}
function getReducedMotionSnapshot() {
  return window.matchMedia(reducedMotionQuery).matches;
}
function getServerReducedMotionSnapshot() {
  return false;
}

export function ReviewCarousel({
  items,
  autoplayMs = 6000,
  ariaLabel = "Customer reviews",
}: {
  items: DisplayReview[];
  /** Auto-scroll cadence in ms. Pass 0 to disable. */
  autoplayMs?: number;
  ariaLabel?: string;
}) {
  const scrollerRef = useRef<HTMLDivElement>(null);
  const [paused, setPaused] = useState(false);
  const [canScrollPrev, setCanScrollPrev] = useState(false);
  const [canScrollNext, setCanScrollNext] = useState(true);
  const reducedMotion = useSyncExternalStore(
    subscribeToReducedMotion,
    getReducedMotionSnapshot,
    getServerReducedMotionSnapshot
  );

  const updateButtonState = useCallback(() => {
    const el = scrollerRef.current;
    if (!el) return;
    setCanScrollPrev(el.scrollLeft > 4);
    setCanScrollNext(el.scrollLeft < el.scrollWidth - el.clientWidth - 4);
  }, []);

  useEffect(() => {
    const el = scrollerRef.current;
    if (!el) return;
    updateButtonState();
    el.addEventListener("scroll", updateButtonState, { passive: true });
    window.addEventListener("resize", updateButtonState);
    return () => {
      el.removeEventListener("scroll", updateButtonState);
      window.removeEventListener("resize", updateButtonState);
    };
  }, [updateButtonState]);

  const scrollByCard = useCallback(
    (direction: 1 | -1) => {
      const el = scrollerRef.current;
      if (!el) return;
      const firstCard = el.querySelector<HTMLElement>("[data-review-card]");
      const step = (firstCard ? firstCard.offsetWidth + CARD_GAP_PX : el.clientWidth) * direction;
      const behavior: ScrollBehavior = reducedMotion ? "auto" : "smooth";
      const max = el.scrollWidth - el.clientWidth;
      if (direction > 0 && el.scrollLeft >= max - 4) {
        el.scrollTo({ left: 0, behavior });
      } else if (direction < 0 && el.scrollLeft <= 4) {
        el.scrollTo({ left: max, behavior });
      } else {
        el.scrollBy({ left: step, behavior });
      }
    },
    [reducedMotion]
  );

  useEffect(() => {
    if (autoplayMs <= 0 || paused || reducedMotion) return;
    if (items.length <= 1) return;
    const interval = setInterval(() => {
      if (document.visibilityState === "hidden") return;
      scrollByCard(1);
    }, autoplayMs);
    return () => clearInterval(interval);
  }, [autoplayMs, paused, reducedMotion, scrollByCard, items.length]);

  if (items.length === 0) return null;

  return (
    <div
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onFocusCapture={() => setPaused(true)}
      onBlurCapture={() => setPaused(false)}
    >
      {/* Controls sit above the row, right-aligned. Hidden on mobile, where
          swiping is the natural gesture (same call as ProductCarouselClient). */}
      {items.length > 1 && (
        <div className="mb-3 hidden justify-end gap-2 sm:flex">
          <button
            type="button"
            onClick={() => scrollByCard(-1)}
            disabled={!canScrollPrev}
            aria-label="Scroll to previous reviews"
            className="inline-flex size-10 items-center justify-center rounded-full border border-border bg-card text-foreground shadow-sm transition-all hover:border-primary hover:text-primary disabled:cursor-not-allowed disabled:opacity-30 disabled:hover:border-border disabled:hover:text-foreground"
          >
            <ChevronLeft className="size-5" />
          </button>
          <button
            type="button"
            onClick={() => scrollByCard(1)}
            disabled={!canScrollNext}
            aria-label="Scroll to next reviews"
            className="inline-flex size-10 items-center justify-center rounded-full border border-border bg-card text-foreground shadow-sm transition-all hover:border-primary hover:text-primary disabled:cursor-not-allowed disabled:opacity-30 disabled:hover:border-border disabled:hover:text-foreground"
          >
            <ChevronRight className="size-5" />
          </button>
        </div>
      )}

      <div
        ref={scrollerRef}
        role="list"
        aria-roledescription="carousel"
        aria-label={ariaLabel}
        className={cn(
          "flex snap-x snap-mandatory items-stretch gap-4 overflow-x-auto pb-1",
          "[scrollbar-width:none] [-ms-overflow-style:none]",
          "[&::-webkit-scrollbar]:hidden"
        )}
      >
        {items.map((review) => (
          <div
            key={review.key}
            data-review-card
            role="listitem"
            className="w-70 shrink-0 snap-start sm:w-82.5 md:w-90"
          >
            {/* `compact` clamps the quote so one long review can't stretch every
                card in the row; the full text is always on /reviews. */}
            <ReviewCard review={review} compact className="h-full" />
          </div>
        ))}
      </div>
    </div>
  );
}
